import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { GraduationCap, ArrowRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useMode } from "@/context/ModeContext";
import Logo from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function JoinClass() {
  const { enroll, formatApiError } = useAuth();
  const { setMode } = useMode();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [form, setForm] = useState({ code: (params.get("code") || "").toUpperCase(), name: "", username: "", password: "" });
  const [loading, setLoading] = useState(false);

  React.useEffect(() => { setMode("kid"); }, [setMode]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = await enroll({ ...form, code: form.code.trim().toUpperCase() });
      setMode("kid");
      toast.success(`Welcome to ${data.class_name}! 🎓`);
      navigate("/app");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not join class");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center p-6 bg-[#05060a]">
      <div className="w-full max-w-sm animate-rise">
        <div className="flex justify-center mb-6"><Logo size="lg" /></div>
        <div className="rounded-3xl border border-white/10 bg-[#0e1117] p-7 brand-glow">
          <div className="w-14 h-14 rounded-2xl grid place-items-center bg-gradient-to-br from-[#1f6feb] to-[#3b9eff] mx-auto mb-4">
            <GraduationCap className="w-7 h-7 text-white" />
          </div>
          <h1 className="font-display text-2xl font-bold text-center text-white">Join your class</h1>
          <p className="text-white/50 text-sm text-center mb-6">Enter the class code from your teacher.</p>
          <form onSubmit={submit} className="space-y-3">
            <div>
              <Label className="text-white">Class code</Label>
              <Input value={form.code} onChange={(e) => set("code", e.target.value.toUpperCase())} placeholder="ABC123" data-testid="join-code"
                className="mt-1.5 bg-white/5 border-white/10 text-white text-center tracking-[0.3em] font-mono text-lg uppercase" maxLength={8} required />
            </div>
            <div>
              <Label className="text-white">Your name</Label>
              <Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="join-name" className="mt-1.5 bg-white/5 border-white/10 text-white" required />
            </div>
            <div>
              <Label className="text-white">Pick a username</Label>
              <Input value={form.username} onChange={(e) => set("username", e.target.value)} data-testid="join-username" className="mt-1.5 bg-white/5 border-white/10 text-white" required />
            </div>
            <div>
              <Label className="text-white">Password</Label>
              <Input value={form.password} onChange={(e) => set("password", e.target.value)} data-testid="join-password" className="mt-1.5 bg-white/5 border-white/10 text-white" required minLength={4} />
            </div>
            <Button type="submit" disabled={loading} data-testid="join-submit"
              className="w-full rounded-full py-6 bg-gradient-to-r from-[#1f6feb] to-[#3b9eff] hover:opacity-90 brand-glow">
              {loading ? "Joining…" : "Join class"} <ArrowRight className="w-4 h-4" />
            </Button>
          </form>
        </div>
        <p className="mt-5 text-sm text-white/50 text-center">
          Have an account? <Link to="/login" className="text-[#3b9eff] font-semibold">Log in</Link>
        </p>
      </div>
    </div>
  );
}
